import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { getEnvVar } from "./util";
import { IIdentify } from "@amplitude/analytics-core";

const APP_ENV = getEnvVar("APP_ENV");
const isProduction = APP_ENV === "prod";
const isDev = APP_ENV === "dev";
const isLocal = APP_ENV === "local";
const isTracked = isProduction;
const API_KEY = getEnvVar("AMPLITUDE_API_KEY");

const debugLog = (msg: string, ...args: unknown[]) => {
  console.log(
    `%c[Amplitude] ${msg}`,
    "color: #6366f1; font-weight: bold; font-size: 12px;",
    ...args,
  );
};

// Amplitude is initialized lazily (not at module load) so that the session
// replay plugin can be registered before amplitude.init() — which is required
// by the Amplitude docs for session replay to attach to the session correctly.
let _amplitudeInitialized = false;
let _replayRequested = false;

const runInit = () => {
  if (_amplitudeInitialized || !isTracked || !API_KEY) return;
  _amplitudeInitialized = true;

  if (_replayRequested) {
    // Plugin MUST be added before amplitude.init() — per Amplitude session replay docs.
    // Adding it after init causes the plugin to miss the session start event.
    if (!isProduction) {
      debugLog(`Session Replay: registering plugin — env=${APP_ENV}, sampleRate=100%`);
    }
    void amplitude.add(
      sessionReplayPlugin({
        // capture 100% of sessions that reach signup, onboarding, or the
        // program-creation wizard — see initSessionReplay() call sites
        sampleRate: 1,
        privacyConfig: {
          maskSelector: ["input[type='password']", "input[type='email']"],
        },
      }),
    );
  }

  amplitude.init(API_KEY, {
    autocapture: {
      pageViews: false, // disabled to prevent wrong titles
      sessions: true, // required — session replay needs session start/end events
      formInteractions: true,
      fileDownloads: true,
      elementInteractions: true, // captures clicks and mouse events for session replay
    },
  });

  if (!isProduction) {
    debugLog(`Initialized — env=${APP_ENV}, key=${API_KEY.slice(0, 8)}…`);
  }
  if (_replayRequested && !isProduction) {
    debugLog("Session Replay: plugin ready — check Amplitude dashboard → Session Replay tab");
  }
};

// Call once at the start of a flow we want visually recorded — signup (step 1),
// onboarding (advisor matching, goal setting, FTUX/code-of-conduct), and the
// program-creation wizard. Subsequent calls (e.g. onboarding right after signup,
// in the same session) are no-ops.
// IMPORTANT: must be called before any trackEvent/identifyUser to guarantee the
// plugin is registered before amplitude.init() — see the WARNING branch below
// for what happens if that ordering is violated.
export const initSessionReplay = () => {
  if (isLocal) {
    debugLog(
      "Session Replay: skipped (local env) — in dev/prod this would start recording the session",
    );
    return;
  }
  if (!isTracked) return;
  if (!API_KEY) {
    if (isDev) debugLog("Session Replay: skipped — AMPLITUDE_API_KEY not configured");
    return;
  }
  if (_replayRequested) {
    if (!isProduction) debugLog("Session Replay: already requested (no-op)");
    return;
  }
  if (_amplitudeInitialized) {
    // Amplitude was already initialized without the replay plugin — this happens
    // when trackEvent/identifyUser fired before initSessionReplay() on this page
    // (e.g. a page that isn't a registered replay entry point ran first in this
    // session). The plugin cannot be attached retroactively.
    if (!isProduction) {
      debugLog(
        "Session Replay: WARNING — Amplitude already initialized without plugin." +
          " Ensure initSessionReplay() is called before any trackEvent/identifyUser calls.",
      );
    }
    return;
  }
  _replayRequested = true;
  runInit();
};

export const trackEvent = (
  name: string,
  props?: Record<string, string | number | boolean | null | undefined>,
) => {
  // Log in local and dev for debugging visibility
  if (isLocal || isDev) {
    console.log(
      `%c[Amplitude Event] ${name}`,
      "color: #6366f1; font-weight: bold; font-size: 12px;",
      "\n",
      JSON.stringify(props, null, 2),
    );
  }
  if (!isTracked) return; // local stops here
  if (!_amplitudeInitialized) runInit(); // lazy init for non-signup pages
  amplitude.track(name, props);
};

export const identifyUser = (userId: string, props?: IIdentify) => {
  if (!isTracked) return;
  if (!_amplitudeInitialized) runInit(); // lazy init
  if (isDev) debugLog(`identifyUser: ${userId}`);
  amplitude.setUserId(userId);
  if (props) amplitude.identify(props);
};

export const logoutUser = () => {
  if (!isTracked) return;
  amplitude.reset();
};
